// New PBX Host modal — provision + register a new verso-agent host.
//
// Submits { name, description, pbx_type, freepbx_client_id?, freepbx_client_secret?,
//   freepbx_base_url? } to POST /agents/provision. Cloud generates the slug +
// hostname automatically (<name>-<org>-<8hex>). Internally the row lives in the
// `agents` table since the verso-agent binary is what actually connects, but
// the user-facing noun is "PBX host".
//
// FreePBX API creds are optional at provision time — set them here for
// one-shot setup, or leave blank and fill in later via Edit.

import { useState } from 'react';
import PbxTypeSelect, { PBX_TYPES } from './PbxTypeSelect.jsx';

const DEFAULT_FREEPBX_BASE_URL = 'http://localhost/admin/api/api/';

export default function NewAgentModal({ onCancel, onSubmit, isPending, error }) {
  const [name, setName]                 = useState('');
  const [description, setDescription]   = useState('');
  const [pbxType, setPbxType]           = useState('freepbx');
  const [clientId, setClientId]         = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [baseUrl, setBaseUrl]           = useState(DEFAULT_FREEPBX_BASE_URL);
  const [showSecret, setShowSecret]     = useState(false);

  const isFreepbx = pbxType === 'freepbx';

  function handleSubmit(e) {
    e.preventDefault();
    if (isPending) return;
    const body = {
      name: name.trim(),
      description: description.trim(),
      pbx_type: pbxType,
    };
    if (isFreepbx) {
      // Only include creds that were actually filled in — empty strings
      // would clobber any pre-existing values (not relevant on create,
      // but keeps semantics consistent with Edit).
      if (clientId.trim())     body.freepbx_client_id     = clientId.trim();
      if (clientSecret)        body.freepbx_client_secret = clientSecret;
      if (baseUrl.trim())      body.freepbx_base_url      = baseUrl.trim();
    }
    onSubmit(body);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">New PBX Host</h3>
        <p className="mt-1 text-sm text-gray-600">
          Verso will provision a Cloudflare tunnel, Access application, DNS record,
          and service token for this host. You&rsquo;ll get a one-time install bundle
          to paste into the verso-agent installer on the PBX.
        </p>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Display name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={80}
              placeholder="e.g. Lyons PBX-01"
              autoFocus
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Becomes the hostname prefix, e.g. &ldquo;PBX 01&rdquo; →
              <span className="ml-1 font-mono">pbx-01-&lt;org&gt;-&lt;random&gt;.verso-cloud.io</span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Physical location, install date, anything to remember"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <PbxTypeSelect value={pbxType} onChange={setPbxType} />

          {/* Aria has no FreePBX API credentials to collect. Its API key is minted locally on the
              host and deliberately kept out of the bundle — install.sh carries over the one
              already there (verso-agent AGENT_ASKS #53). Saying so stops the empty section
              reading as a form that failed to load. */}
          {!isFreepbx && pbxType === 'aria' && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              No credentials to enter here. The installer picks up the Aria API key already on the
              host — it is minted there and never travels in the bundle. Verso reads this host and
              does not write to it yet.
            </div>
          )}
          {isFreepbx && (
            <fieldset className="rounded-md border border-gray-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {PBX_TYPES.freepbx.label} API (optional)
              </legend>
              <p className="text-[11px] text-gray-500 mb-2">
                If you&rsquo;ve already created a Machine-to-Machine API
                Application in FreePBX, paste the creds here. Otherwise leave
                blank &mdash; you can add them later via Edit.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700">Client ID</label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="128-char client_id"
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="mt-3">
                <label className="flex items-center justify-between text-xs font-medium text-gray-700">
                  <span>Client Secret</span>
                  <button
                    type="button"
                    onClick={() => setShowSecret((s) => !s)}
                    className="text-xs font-normal text-blue-600 hover:underline"
                  >
                    {showSecret ? 'Hide' : 'Show'}
                  </button>
                </label>
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="64-char client_secret"
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoComplete="new-password"
                />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700">Base URL</label>
                <input
                  type="text"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={DEFAULT_FREEPBX_BASE_URL}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </fieldset>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || name.trim().length < 2}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Provisioning…' : 'Provision PBX host'}
            </button>
          </div>
          {isPending && (
            <p className="text-xs text-gray-500">
              Creating tunnel, DNS record, Access app, service token, and policy. This can take
              up to a minute.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
